import { CheckCheck, ClipboardList, CircleDashed, Send, Undo2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { Tooltip } from '../../../../components/Tooltip';
import { CountBadge } from '../../../../components/CountBadge';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { useToast } from '../../../../components/toastContext';
import { IssuanceModal } from '../../../logistics/inventory/components/IssuanceModal';
import { IssuedAssetsPanel } from '../../../logistics/inventory/components/IssuedAssetsPanel';
import { RecordReturnModal } from '../../../logistics/inventory/components/RecordReturnModal';
import { issuancesApi } from '../../../logistics/inventory/issuanceApi';
import type { IssuanceDetail, IssuedAssetLine } from '../../../logistics/inventory/issuanceTypes';
import { CancelRequisitionSlipModal } from '../components/CancelRequisitionSlipModal';
import { RequisitionSlipLinesPanel } from '../components/RequisitionSlipLinesPanel';
import { RequisitionSlipModal } from '../components/RequisitionSlipModal';
import { issuanceRequestsApi } from '../../../logistics/inventory/issuanceRequestApi';
import { requisitionSlipsApi } from '../requisitionSlipsApi';
import { RIS_STAGE } from '../requisitionSlipTypes';
import type { RequisitionSlipDetail } from '../requisitionSlipTypes';

type TabKey = 'info' | 'lines' | 'issued-assets';

/**
 * One requisition slip.
 *
 * **IT LANDS ON LINES**, which is the document-profile exemption reaching its sixth record: a
 * reader opens a requisition to see WHAT WAS ASKED FOR, where every other profile is opened to
 * check a record's own fields. `Info` is still the first tab DECLARED, so the strip's order
 * and the check `<ProfileHeaderShell>` makes in development are untouched — only where the
 * page lands.
 *
 * **TWO SURFACES, ONE PAGE** (03 Nov 2026). `self-service` is the requester's own slip, with
 * the lifecycle actions; `logistics` is the same record opened from Issuances › Requests,
 * READ-ONLY — fetched through that tab's own endpoint on `issuances.read-requests`, with no
 * Submit, no Withdraw and no Edit, because correcting a slip is its requester's act and the
 * write endpoints are scoped to them.
 */
export function RequisitionSlipProfilePage({
  surface = 'self-service',
}: {
  surface?: 'self-service' | 'logistics';
}) {
  const { id = '' } = useParams();
  const isLogistics = surface === 'logistics';

  const [slip, setSlip] = useState<RequisitionSlipDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('lines');

  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acting, setActing] = useState(false);

  /*
    ISSUED ASSETS — the Logistics surface only (03 Nov 2026): every article released AGAINST this
    request, on `issuances.read` (the key of what the rows are). Fetched with the page, so the
    tab's count is right before it is opened; the requester's own surface never asks.
  */
  const toast = useToast();
  const canReadIssuances = usePermission('issuances.read');
  const showsIssued = isLogistics && canReadIssuances;

  const [issued, setIssued] = useState<IssuedAssetLine[] | null>(null);
  const [issuedFailed, setIssuedFailed] = useState<string | null>(null);
  const [issuedReloadKey, setIssuedReloadKey] = useState(0);
  const [issuing, setIssuing] = useState(false);
  const [returning, setReturning] = useState<{ issuance: IssuanceDetail; lineId: string } | null>(null);

  useEffect(() => {
    if (!showsIssued) return;

    const controller = new AbortController();

    issuanceRequestsApi
      .issuedAssets(id, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setIssued(next);
        setIssuedFailed(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setIssuedFailed(caught instanceof ApiError ? caught.message : 'Could not load the issued assets.');
      });

    return () => controller.abort();
  }, [id, showsIssued, issuedReloadKey]);

  async function openReturn(row: IssuedAssetLine) {
    try {
      setReturning({ issuance: await issuancesApi.get(row.issuanceId), lineId: row.id });
    } catch (caught) {
      // No form is open yet to report in — a toast, never the panel's load error.
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not open that issuance.');
    }
  }

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setSlip(await (isLogistics ? issuanceRequestsApi : requisitionSlipsApi).get(id, signal));
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load this slip.', 0));
      }
    },
    [id, isLogistics],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function submit() {
    setActing(true);
    setError(null);

    try {
      setSlip(await requisitionSlipsApi.submit(id));
      setSubmitting(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not submit this slip.', 0));
    } finally {
      setActing(false);
    }
  }

  if (error !== null && slip === null) {
    return (
      <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-3">
        <p className="text-xs text-red-700">{error.message}</p>
      </div>
    );
  }

  if (slip === null) {
    return null;
  }

  const live = slip.items.filter((line) => line.isActive);
  const banner = bannerFor(slip, surface);
  const BannerIcon = banner.icon;

  const breadcrumb: BreadcrumbSegment[] = isLogistics
    ? [
        'Logistics',
        'Inventory',
        { label: 'Issuances', to: '/inventory/issuances' },
        { label: 'Requests', to: '/inventory/issuances/requests' },
        slip.slipNumber,
      ]
    : [
        'Self Service',
        'Inventory',
        { label: 'Requisition and Issue Slip', to: '/self-service/requisition-slips' },
        slip.slipNumber,
      ];

  return (
    <>
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="requisition-slip-profile"
        /*
          THE PANEL MODE IS PER TAB. Info is a stack of cards and takes the capped `cards`
          measure; the Lines tab is a table owning its own chrome and scroll, which is what
          `bleed` is for.
        */
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="requisition-slip-profile-header"
            /*
              THE MODULE'S OWN GLYPH IN THE DISC — the sidebar entry's `ClipboardList`, so the
              header and the nav row the reader came from carry one symbol.
            */
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={slip.slipNumber}
            meta={[slip.requestingOrgUnitName].filter(Boolean) as string[]}
            secondary={[
              `${live.length} ${live.length === 1 ? 'line' : 'lines'}`,
              slip.risNumber ? `RIS no. ${slip.risNumber}` : null,
            ].filter(Boolean) as string[]}
            status={
              <StatusText tone={slip.isActive ? 'active' : 'inactive'}>{slip.stageLabel}</StatusText>
            }
            createdAt={slip.createdAt}
            createdBy={slip.createdByName}
          />
        }
        banner={
          /*
            THE RECORD-LEVEL LIFECYCLE ACTIONS LIVE HERE, and it renders for EVERY state rather
            than only the actionable ones — a strip that appears only when something needs
            doing is one the reader learns to skip.
          */
          <NextActionAlert
            icon={<BannerIcon className="h-4 w-4" aria-hidden="true" />}
            title={banner.title}
            description={banner.description}
            action={
              isLogistics ? undefined : (
              <div className="flex items-center gap-2">
                {slip.canSubmit && slip.isActive ? (
                  <PermissionGate permission="requisition-slips.submit">
                    {/*
                      NO `block` on the tooltip — this sits in a flex ROW, where `w-full` would
                      be 100% of the container and the button would take the whole strip. The
                      test is the CHILD, and a `<Button>` is `inline-flex`.
                    */}
                    <Tooltip
                      disabledReason={
                        live.length === 0 ? 'Add at least one item before submitting.' : undefined
                      }
                    >
                      <Button
                        id="requisition-slip-submit"
                        variant="primary"
                        leading={<Send className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setSubmitting(true)}
                        disabled={acting || live.length === 0}
                      >
                        Submit
                      </Button>
                    </Tooltip>
                  </PermissionGate>
                ) : null}

                {slip.canCancel && slip.isActive ? (
                  <PermissionGate permission="requisition-slips.cancel">
                    <Button
                      id="requisition-slip-cancel"
                      variant="secondary"
                      leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setCancelling(true)}
                      disabled={acting}
                    >
                      Withdraw
                    </Button>
                  </PermissionGate>
                ) : null}
              </div>
              )
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Lines <CountBadge count={live.length} />
              </>
            ),
          },
          ...(isLogistics
            ? [
                {
                  key: 'issued-assets',
                  label: (
                    <>
                      Issued Assets <CountBadge count={issued?.filter((x) => x.isActive).length} />
                    </>
                  ),
                  // The key of what the panel SHOWS — issuance lines — never the page it is on.
                  permission: 'issuances.read',
                },
              ]
            : []),
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {error !== null ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {tab === 'info' ? (
          <>
            <ProfileCard
              id="requisition-slip-profile-request"
              title="Request"
              // The requester's card to correct; on the Logistics surface it is another office's record.
              onEdit={isLogistics ? undefined : () => setEditing(true)}
              editPermission="requisition-slips.update"
              /*
                DISABLED FOR STATE, not hidden: a reader may correct a slip, and what stops
                them is that this one has moved on. `editPermission` hides, which would say
                they lack a grant.
              */
              editDisabledReason={
                slip.isEditable ? undefined : 'Only a draft can be corrected. Withdraw it and raise another.'
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Slip number', value: slip.slipNumber, mono: true },
                  { label: 'RIS number', value: slip.risNumber, mono: true },
                  { label: 'Requesting office', value: slip.requestingOrgUnitName },
                  { label: 'Purpose', value: slip.purpose },
                  { label: 'Notes', value: slip.notes },
                  { label: 'Withdrawn because', value: slip.cancellationReason },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="requisition-slip-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created', value: formatDateTime(slip.createdAt), mono: true },
                  { label: 'Created by', value: slip.createdByName },
                  {
                    label: 'Latest update',
                    value: slip.updatedAt == null ? null : formatDateTime(slip.updatedAt),
                    mono: true,
                  },
                  { label: 'Updated by', value: slip.updatedByName },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'issued-assets' ? (
          // The same inset the Lines tab takes — `fill` distributes the height it is GIVEN.
          <div className="flex h-full min-w-0 flex-col p-4">
            <IssuedAssetsPanel
              id="requisition-request-issued-assets"
              fill
              rows={issued}
              failed={issuedFailed}
              employeeIsActive
              onIssue={() => setIssuing(true)}
              issueDisabledReason={
                !slip.isActive
                  ? 'This slip is deleted.'
                  : slip.stage !== RIS_STAGE.approved
                    ? 'Only an approved request can be issued against. This one is still waiting for endorsement.'
                    : null
              }
              emptyMessage="Nothing has been issued against this request yet."
              onReturn={(row) => void openReturn(row)}
            />
          </div>
        ) : (
          /*
            A `bleed` panel gives its child NO gap, so the inset belongs to this wrapper — the
            search group's border would otherwise sit on the panel's and the two read as one
            control that has come apart. `h-full` and `flex-col` are load-bearing through it:
            `<DataTablePanel fill>` distributes the height it is GIVEN.
          */
          <div className="flex h-full min-w-0 flex-col p-4">
            <RequisitionSlipLinesPanel slip={slip} />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <RequisitionSlipModal
          editing={slip}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setEditing(false);
            setSlip(saved);
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelRequisitionSlipModal
          slipId={slip.id}
          slipNumber={slip.slipNumber}
          isSubmitted={slip.stage === RIS_STAGE.submitted}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            void load();
          }}
        />
      ) : null}

      {issuing ? (
        <IssuanceModal
          open
          requisitionSlip={{ id: slip.id, slipNumber: slip.slipNumber }}
          // The requester is accountable for what their office asked for — seeded, not locked.
          initialAccountablePersonnelId={slip.accountablePersonnelId ?? null}
          onClose={() => setIssuing(false)}
          onSaved={() => {
            setIssuing(false);
            setIssuedReloadKey((k) => k + 1);
            toast.success(`Stock issued against ${slip.slipNumber}.`);
          }}
        />
      ) : null}

      {returning ? (
        <RecordReturnModal
          open
          issuance={returning.issuance}
          initialLineId={returning.lineId}
          onClose={() => setReturning(null)}
          onSaved={() => {
            setReturning(null);
            setIssuedReloadKey((k) => k + 1);
            toast.success('Return recorded.');
          }}
        />
      ) : null}

      {submitting ? (
        <ConfirmDialog
          open
          id="requisition-slip-submit-dialog"
          title={`Submit ${slip.slipNumber}`}
          /*
            EVERY BANNER ACTION CONFIRMS. Submitting hands the record to somebody else, and the
            tick makes the reader touch a different control and read the sentence beside it.

            NO `requireCredential` — submitting a slip is an ordinary act of the job, and
            password ceremony on something done weekly is ceremony people click through.
          */
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                This hands the slip to its approver and takes it out of draft, so its lines and
                details can no longer be corrected. Getting it back is a withdrawal and a fresh
                slip, not an undo.
              </p>
              {error ? <p className="text-red-700">{error.message}</p> : null}
            </div>
          }
          loading={acting}
          onConfirm={() => void submit()}
          onClose={() => setSubmitting(false)}
        />
      ) : null}
    </>
  );
}

/**
 * What the banner says for EVERY stage — there is no null case.
 *
 * **The waiting states are the whole reason it is mandatory**: a slip sitting at Approved with
 * nothing happening to it is the one a requester most needs explained, and a
 * render-only-when-actionable banner is precisely what hides them.
 */
function bannerFor(
  slip: RequisitionSlipDetail,
  surface: 'self-service' | 'logistics',
): {
  tone: 'blue' | 'emerald' | 'amber' | 'slate' | 'red';
  icon: typeof ClipboardList;
  title: string;
  description: string;
} {
  if (!slip.isActive) {
    return {
      tone: 'slate',
      icon: XCircle,
      title: 'Deleted',
      description: 'This slip was raised in error and has been deleted. Restoring it brings it back with its number and history intact.',
    };
  }

  switch (slip.stage) {
    case RIS_STAGE.draft:
      return {
        tone: 'blue',
        icon: CircleDashed,
        title: 'Draft — not yet submitted',
        description: 'Add what the office needs, then submit it for endorsement. Nothing is reserved and no stock moves.',
      };

    case RIS_STAGE.submitted:
      if (surface === 'logistics') {
        return {
          tone: 'blue',
          icon: Send,
          title: 'Waiting for endorsement',
          description: 'The requesting office has sent this slip and it is with its approver. Nothing is reserved yet.',
        };
      }

      return {
        tone: 'blue',
        icon: Send,
        title: 'Waiting for endorsement',
        description: 'This slip is with its approver. Clearing it reserves nothing — stock is drawn when it is released.',
      };

    case RIS_STAGE.approved:
      // Said to the STOREKEEPER on the Logistics surface — the gap is the same, the reader is not.
      if (surface === 'logistics') {
        return {
          tone: 'amber',
          icon: CheckCheck,
          title: 'Endorsed — ready to issue',
          description:
            'An approver has endorsed this request. Release the stock from the Issued Assets tab; the slip stays Approved, since nothing yet counts a release as fulfilling it.',
        };
      }

      /*
        THE PHASE-1 GAP, STATED BEFORE THE READER GOES LOOKING. An approved slip cannot be
        fulfilled yet: there is no intake, no Supply clearance and no issuance bridge, so it
        stops here. Amber rather than emerald — approved is not finished, and emerald would
        say the opposite. The honest alternative is a storekeeper releasing the stock through
        Logistics › Inventory › Issuances and this slip staying Approved.
      */
      return {
        tone: 'amber',
        icon: CheckCheck,
        title: 'Endorsed — fulfilment is not built yet',
        description:
          'An approver has endorsed this slip. Releasing against it is not built, so it stays here: a storekeeper issues the stock through Logistics › Inventory › Issuances in the meantime.',
      };

    case RIS_STAGE.rejected:
      return {
        tone: 'red',
        icon: XCircle,
        title: 'Not endorsed',
        description: 'An approver refused this slip. The reason is on the approval it was routed through; raising a fresh slip is the way forward.',
      };

    default:
      return {
        tone: 'slate',
        icon: Undo2,
        title: 'Withdrawn',
        description: slip.cancellationReason
          ? `Withdrawn by the requesting office: ${slip.cancellationReason}`
          : 'Withdrawn by the requesting office before anything was released.',
      };
  }
}
